import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Pagination, Button, Avatar } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { getProjectTemplateIconUrl, Permission } from "@octopusdeploy/octopus-server-client";
import type { Repository, PlatformHubConnectionConfigurationResource, ValidateGitRefV2Response, GitRef, GetProjectTemplatesBffResponse, ProjectTemplateSummary } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import * as React from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { AddProjectTemplateDialog } from "~/areas/platformHub/projectTemplates/AddProjectTemplateDialog";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import InternalLink from "~/components/Navigation/InternalLink";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable";
import DateFormatter from "~/utils/DateFormatter/index";
interface LoaderData {
    projectTemplatesResponse: GetProjectTemplatesBffResponse;
    platformHubConnectionConfiguration: PlatformHubConnectionConfigurationResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
}
interface ProjectTemplatesPageProps {
    loaderData: LoaderData;
    queryParams: ProjectTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectTemplatesPageQueryParams>;
}
export interface ProjectTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyProjectTemplatesResponse: GetProjectTemplatesBffResponse = {
    ProjectTemplates: [],
    TotalResults: 0,
    ItemsPerPage: 0,
    TotalNoOfProjectTemplates: 0,
};
export async function projectTemplatesPageLoader(repository: Repository, queryParams: ProjectTemplatesPageQueryParams): Promise<LoaderData> {
    const platformHubConnectionConfiguration = await repository.PlatformHubConnection.get();
    if (!platformHubConnectionConfiguration.PersistenceSettings.Url) {
        return {
            projectTemplatesResponse: emptyProjectTemplatesResponse,
            platformHubConnectionConfiguration,
            initialValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(platformHubConnectionConfiguration.PersistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    const userPageSize = getValueInStorage(`Octopus.ProjectTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const projectTemplatesResponse = await repository.ProjectTemplates.getPaginatedTemplates({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: initialValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        projectTemplatesResponse,
        platformHubConnectionConfiguration,
        initialValidationResult,
    };
}
export function ProjectTemplatesPage({ loaderData, queryParams, setQueryParams }: ProjectTemplatesPageProps) {
    const [gitRef, setGitRef] = usePlatformHubGitRef(loaderData.platformHubConnectionConfiguration.PersistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProjectTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyProjectTemplatesResponse);
        }
        return repository.ProjectTemplates.getPaginatedTemplates({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize], "Get paginated project templates", {
        initialResult: loaderData.projectTemplatesResponse,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const { execute: deleteProjectTemplate } = useMutation({
        name: "Delete Project Template",
        action: (repository: Repository, projectTemplate: ProjectTemplateSummary) => repository.ProjectTemplates.del({ Id: projectTemplate.Id, ChangeDescription: `Delete Project Template '${projectTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    if (!gitRef || !loaderData.platformHubConnectionConfiguration.PersistenceSettings.Url) {
        return (<PageContent header={{ title: "Templates" }}>
                <ConfigureVersionControlOnboarding />
            </PageContent>);
    }
    const addProjectTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Add Project Template",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <AddProjectTemplateButton gitRef={gitRef} reload={reload}/>,
    };
    const showOnboarding = pagingData.TotalNoOfProjectTemplates === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} gitPersistenceSettings={loaderData.platformHubConnectionConfiguration.PersistenceSettings}/>;
    if (showOnboarding) {
        return (<PageContent header={{ title: "Templates", contextSelector: titleAccessory }}>
                <AddProjectTemplatesOnboarding gitRef={gitRef} reload={reload}/>
            </PageContent>);
    }
    return (<PageContent header={{ title: "Templates", primaryAction: addProjectTemplateAction, contextSelector: titleAccessory }} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Project Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <DataTable>
                <DataTableHeader>
                    <DataTableRow>
                        <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                        <DataTableHeaderColumn className={cn(styles.descriptionColumn)}>Description</DataTableHeaderColumn>
                        <DataTableHeaderColumn>Last Published Version</DataTableHeaderColumn>
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {pagingData.ProjectTemplates.map((pt) => (<ProjectTemplateRow projectTemplate={pt} deleteProjectTemplate={deleteProjectTemplate} key={pt.Id}/>))}
                </DataTableBody>
            </DataTable>
        </PageContent>);
}
//TODO put correct link to docs in callout
const AddProjectTemplatesOnboarding = ({ gitRef, reload }: {
    gitRef: GitRef;
    reload: () => void;
}) => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects." learnMore={null} actionButtons={<AddProjectTemplateButton gitRef={gitRef} reload={reload}/>}/>);
const ConfigureVersionControlOnboarding = () => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects. Project templates are stored in a Git repository. To add a project template, you'll first need to set up a Git connection in the Platform Hub." learnMore={null} actionButtons={<Button label={"Configure Version Control"} href={links.platformHubVersionControlSettingsPage.generateUrl()} importance="primary"/>}/>);
interface ProjectTemplateRowProps {
    projectTemplate: ProjectTemplateSummary;
    deleteProjectTemplate: (projectTemplate: ProjectTemplateSummary) => Promise<void>;
}
function ProjectTemplateRow({ projectTemplate, deleteProjectTemplate }: ProjectTemplateRowProps) {
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <ProjectTemplateNameCell projectTemplate={projectTemplate}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <div className={styles.rowCellPadding}>{projectTemplate.Description}</div>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                {projectTemplate.Version && projectTemplate.PublishedDate ? (<div className={styles.rowCellPadding}>
                        {projectTemplate.Version} ({DateFormatter.dateToLongFormat(projectTemplate.PublishedDate)})
                    </div>) : null}
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.right}>
                <div className={styles.rowCellPadding}>
                    <ProjectTemplateOverflowMenu projectTemplate={projectTemplate} deleteProjectTemplate={deleteProjectTemplate}/>
                </div>
            </DataTableRowColumn>
        </DataTableRow>);
}
interface ProjectTemplateNameCellProps {
    projectTemplate: ProjectTemplateSummary;
}
function ProjectTemplateNameCell({ projectTemplate }: ProjectTemplateNameCellProps) {
    const linkTarget = links.phBranchDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef: projectTemplate.GitRef });
    return (<div className={styles.nameContainer}>
            <InternalLink className={styles.nameContainer} to={linkTarget}>
                <Avatar type="template" size="medium" src={getProjectTemplateIconUrl(projectTemplate)} alt="Project template icon"/>
                <div className={styles.name}>{projectTemplate.Name}</div>
            </InternalLink>
        </div>);
}
interface ProjectTemplateOverflowProps {
    projectTemplate: ProjectTemplateSummary;
    deleteProjectTemplate: (projectTemplate: ProjectTemplateSummary) => Promise<void>;
}
function ProjectTemplateOverflowMenu({ projectTemplate, deleteProjectTemplate }: ProjectTemplateOverflowProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    //overFlowActions.push(OverflowMenuItems.deleteItem("Delete", "Are you sure you want to delete this Project Template?", () => deleteProjectTemplate(projectTemplate), <DeleteProjectTemplate projectTemplateName={projectTemplate.Name} />));
    return <OverflowMenu menuItems={overFlowActions}/>;
}
function AddProjectTemplateButton({ gitRef, reload }: {
    gitRef: GitRef;
    reload: () => void;
}) {
    return (<OpenDialogButton disabled={false} label={"Add Project Template"} title={"Create new project template"} type={ActionButtonType.Primary}>
            <AddProjectTemplateDialog gitRef={gitRef} onComplete={reload}/>
        </OpenDialogButton>);
}
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", alignItems: "center", gap: space[12] }),
    name: css({ paddingLeft: space[6] }),
    descriptionColumn: css({
        width: "40%",
    }),
};
